import styled, { css, keyframes } from 'styled-components';

/* ─── Overlay (absorbed from SwapOverlay) ─── */

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: 96px;

  & > * {
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    padding-bottom: 132px;
  }
`;

export const PanelWrapper = styled.div`
  width: calc(100vw - 16px);
  max-width: 460px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 50, 50, 0.4);
    border-color: rgba(255, 100, 100, 0.6);
  }
`;

export const Panel = styled.div`
  width: 100%;
  background: rgba(10, 8, 30, 0.75);
  border: 1px solid rgba(155, 89, 182, 0.3);
  border-radius: 16px;
  padding: 20px;
  backdrop-filter: blur(20px);
  box-shadow:
    0 0 40px rgba(100, 50, 200, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);

  @media (max-width: 768px) {
    padding: 14px;
    border-radius: 12px;
  }
`;

/* ─── Swap form ─── */

export const Block = styled.div`
  margin-bottom: 4px;
`;

export const BlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

export const BlockTitle = styled.span`
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
`;

export const Balance = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
`;

export const MaxButton = styled.button`
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(155, 89, 182, 0.15);
  border: 1px solid rgba(155, 89, 182, 0.3);
  color: rgba(155, 89, 182, 0.8);
  font-family: 'Share Tech Mono', monospace;
  font-size: 9px;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;

  &:hover {
    background: rgba(155, 89, 182, 0.25);
    border-color: rgba(155, 89, 182, 0.5);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const TokenLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 8px 10px;
  transition: border-color 0.3s;

  &:focus-within {
    border-color: rgba(155, 89, 182, 0.5);
  }
`;

export const TokenInput = styled.input<{ $isWarning?: boolean }>`
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  outline: none;
  font-family: 'Share Tech Mono', monospace;
  font-size: 18px;
  color: ${({ $isWarning }) => ($isWarning ? 'rgba(231, 76, 60, 0.9)' : '#fff')};
  font-weight: bold;
  text-align: right;

  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

export const TokenOutput = styled.div`
  flex: 1;
  min-width: 0;
  font-family: 'Share Tech Mono', monospace;
  font-size: 18px;
  color: rgba(46, 204, 113, 0.9);
  font-weight: bold;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-height: 18px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

export const TokenButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  font-weight: normal;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(155, 89, 182, 0.5);
  }

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 4px 8px;
  }
`;

export const Chevron = styled.span`
  font-size: 13px;
  opacity: 0.5;
`;

export const TokenSubLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  min-height: 16px;
`;

export const TokenAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const TokenLink = styled.a`
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  transition: 0.15s;

  &:hover {
    color: rgba(255, 255, 255, 0.7);
  }
`;

export const TokenNativeLabel = styled.div`
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
`;

export const CopyButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  margin: -2px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 14px;
  transition: 0.15s;
  display: flex;
  align-items: center;

  &:hover {
    color: rgba(255, 255, 255, 0.7);
  }
`;

export const TokenValueInDollars = styled.span`
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
`;

export const FlipButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 4px auto;
  border-radius: 50%;
  background: rgba(155, 89, 182, 0.15);
  border: 1px solid rgba(155, 89, 182, 0.3);
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: rgba(155, 89, 182, 0.3);
    border-color: rgba(155, 89, 182, 0.6);
    transform: rotate(180deg);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      transform: none;
    }
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const SwapButton = styled.button<{ $disabled?: boolean; $isLoading?: boolean }>`
  display: block;
  width: 100%;
  margin-top: 14px;
  padding: 12px;
  background: linear-gradient(135deg, rgba(155, 89, 182, 0.7), rgba(52, 152, 219, 0.7));
  border: none;
  border-radius: 10px;
  color: #fff;
  font-family: 'Share Tech Mono', monospace;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 2px;
  box-shadow: 0 0 20px rgba(155, 89, 182, 0.2);
  text-align: center;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s;

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      animation: ${pulse} 1.5s ease-in-out infinite;
    `}

  ${({ $disabled }) =>
    $disabled
      ? css`
          opacity: 0.45;
          cursor: not-allowed;
        `
      : css`
          &:hover {
            opacity: 0.9;
            background: linear-gradient(135deg, rgba(155, 89, 182, 0.8), rgba(52, 152, 219, 0.8));
            box-shadow:
              0 0 30px rgba(155, 89, 182, 0.5),
              0 0 60px rgba(52, 152, 219, 0.3);
          }
        `}

  @media (max-width: 768px) {
    padding: 10px;
    font-size: 13px;
  }
`;

export const Message = styled.div<{ $type: 'warning' | 'danger' | 'info' }>`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;

  ${({ $type }) => {
    switch ($type) {
      case 'danger':
        return css`
          background: rgba(231, 76, 60, 0.15);
          border: 1px solid rgba(231, 76, 60, 0.3);
          color: rgba(231, 76, 60, 0.9);
        `;
      case 'warning':
        return css`
          background: rgba(241, 196, 15, 0.1);
          border: 1px solid rgba(241, 196, 15, 0.3);
          color: rgba(241, 196, 15, 0.9);
        `;
      default:
        return css`
          background: rgba(52, 152, 219, 0.1);
          border: 1px solid rgba(52, 152, 219, 0.3);
          color: rgba(52, 152, 219, 0.9);
        `;
    }
  }}
`;

export const SwapDetailsWrapper = styled.div`
  margin-top: 8px;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ApproveCheckbox = styled.div`
  flex-shrink: 0;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px;
  gap: 6px;
`;

export const SettingsButton = styled.button`
  position: relative;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    transition: fill 0.15s;

    path {
      fill: rgba(255, 255, 255, 0.7);
    }
  }

  &:hover {
    svg path {
      fill: rgba(255, 255, 255, 0.9);
    }
  }

  .settings-button__warning {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 8px;
    color: rgba(231, 76, 60, 0.9);
  }
`;

export const ButtonInText = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline !important;
  text-decoration: underline;
  color: inherit;
  font: inherit;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;
